/**
 * Scroll Intro Modules
 * 复杂的滚动驱动动画系统
 *
 * 滚动阶段：
 * - Phase 0 (0-20%): 文字合并 + 图片放大
 * - Phase 1 (20-30%): 显示模块界面
 * - Phase 2+ (30-100%): 模块切换
 */

// 常量定义
const CONSTANTS = {
  ASPECT_RATIO: 16 / 9,
  VIDEO_START_WIDTH: 140,
  VIDEO_MAX_WIDTH: 700,
  VIDEO_MAX_WIDTH_RATIO: 0.6,
  TEXT_SPACING: 20,
  BORDER_RADIUS: 12,
  MOBILE_BREAKPOINT: 768,
  MARQUEE_REPEATS: 8,
  SNAP_DEBOUNCE: 120
};

class ScrollIntroModules {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;

    // DOM 元素
    this.viewport = section.querySelector('.scroll-intro-modules__viewport');
    this.scrollContainer = section.querySelector('.scroll-intro-modules__scroll-container');
    this.bgVideos = section.querySelectorAll('.scroll-intro-modules__bg-video');
    this.bgImages = section.querySelectorAll('.scroll-intro-modules__bg-image');
    this.centerMedia = section.querySelector('.scroll-intro-modules__center-media');
    this.videoPlaceholder = section.querySelector('.scroll-intro-modules__video-placeholder');
    this.centerVideo = section.querySelector('.scroll-intro-modules__center-video');
    this.centerImage = section.querySelector('.scroll-intro-modules__center-image');
    this.initialText = section.querySelector('.scroll-intro-modules__initial-text');
    this.textGroups = section.querySelectorAll('[data-text-group]');
    this.modulesContent = section.querySelector('.scroll-intro-modules__modules-content');
    this.navTitles = section.querySelectorAll('[data-nav-title]');
    this.navThumbs = section.querySelectorAll('[data-nav-thumb]');
    this.moduleDetails = section.querySelectorAll('[data-module-detail]');
    this.thumbVideos = section.querySelectorAll('.scroll-intro-modules__thumb-video');
    this.currentTitleEl = section.querySelector('[data-current-title]');

    // Modal 元素
    this.modal = section.querySelector('.scroll-intro-modal[data-modal]');
    this.modalOverlay = section.querySelector('.scroll-intro-modal [data-modal-overlay]');
    this.modalClose = section.querySelector('.scroll-intro-modal [data-modal-close]');
    this.modalContent = section.querySelector('.scroll-intro-modal [data-modal-content]');
    this.modalContainer = section.querySelector('.scroll-intro-modal .scroll-intro-modal__container');
    this.modalTriggers = section.querySelectorAll('[data-modal-trigger]');

    // 状态
    this.currentTextGroup = 0;
    // 模块数量 = 视频数量 + 图片数量（因为它们是互斥的）
    this.moduleCount = Math.max(this.bgVideos.length, this.bgImages.length);
    this.currentModule = 0;
    this.scrollProgress = 0;
    this.ticking = false;
    this.centerVideoPlaying = false;
    this.centerVideoReady = false;

    // 缓存 DOM 查询结果
    this._cachedThumbItems = null;
    this._cachedTextElements = null;
    this._cachedBgContainer = null;
    this._windowSize = { width: 0, height: 0 };
    this._lastWindowSizeCheck = 0;

    // 高度配置
    this.screenHeights = {
      textMerge: 200,
      showModules: 100,
      perModule: 100
    };

    this.totalHeight = 0;
    this.phases = {};
    this.variant = this.section.dataset.variant || 'studio';

    this.init();
  }

  init() {
    this.isMobile = window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT;

    if (this.isMobile) {
      this.initMobile();
      this.bindModalEvents();
    } else {
      this.setupScrollHeight();
      this.bindScrollEvent();
      this.bindNavigationEventsMirrored();
      this.bindModalEvents();
      this.initVideos();
      this.activateTextGroup(0);
      this.handleScroll();
    }

    this.section.classList.add('is-loaded');
    this.section.classList.remove('is-loading');

    // 保存 resize 监听器引用以便清理
    this._resizeHandler = () => {
      const newIsMobile = window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT;
      if (newIsMobile !== this.isMobile) {
        location.reload();
      }
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  // ===== 滚动高度设置 =====
  setupScrollHeight() {
    if (!this.scrollContainer) return;

    this.totalHeight =
      this.screenHeights.textMerge +
      this.screenHeights.showModules +
      (this.moduleCount * this.screenHeights.perModule);

    // 防止除零错误
    if (this.totalHeight <= 0) {
      this.totalHeight = 100;
    }

    this.scrollContainer.style.minHeight = `${this.totalHeight}vh`;

    const textMergeEnd = this.screenHeights.textMerge / this.totalHeight;
    const showModulesEnd = (this.screenHeights.textMerge + this.screenHeights.showModules) / this.totalHeight;

    this.phases = {
      textMerge: { start: 0, end: textMergeEnd, heightVh: this.screenHeights.textMerge },
      showModules: { start: textMergeEnd, end: showModulesEnd, heightVh: this.screenHeights.showModules },
      modules: { start: showModulesEnd, end: 1, heightVh: this.moduleCount * this.screenHeights.perModule }
    };
  }

  // ===== 事件绑定 =====
  bindScrollEvent() {
    const handleScroll = () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    this._scrollHandler = handleScroll;
  }

  bindNavigationEventsMirrored() {
    this.navTitles.forEach(title => {
      title.addEventListener('click', (e) => {
        const index = parseInt(e.currentTarget.dataset.moduleIndex, 10);
        if (isNaN(index)) return;
        const progress = this.getScrollPositionForModule(index);
        if (progress != null) {
          this.scrollToProgress(progress);
        } else {
          this.switchToModule(index);
        }
      });
    });

    this.navThumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        const targetIndex = parseInt(e.currentTarget.dataset.moduleIndex, 10);
        if (isNaN(targetIndex)) return;
        const progress = this.getScrollPositionForModule(targetIndex);
        if (progress != null) {
          this.scrollToProgress(progress);
        } else {
          this.switchToModule(targetIndex);
        }
      });
    });
  }

  bindModalEvents() {
    if (!this.modal) return;

    this.modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openModal(trigger);
      });
    });

    if (this.modalClose) {
      this.modalClose.addEventListener('click', () => this.closeModal());
    }

    if (this.modalOverlay) {
      this.modalOverlay.addEventListener('click', () => this.closeModal());
    }

    if (this.modalContainer) {
      this.modalContainer.addEventListener('click', (e) => e.stopPropagation());
    }

    this._modalClickHandler = (e) => {
      const clickedInsideContainer = this.modalContainer && this.modalContainer.contains(e.target);
      const clickedOverlay = this.modalOverlay && e.target === this.modalOverlay;
      if (!clickedInsideContainer || clickedOverlay) {
        this.closeModal();
      }
    };
    this.modal.addEventListener('click', this._modalClickHandler);

    this._modalKeyHandler = (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    };
    document.addEventListener('keydown', this._modalKeyHandler);
  }

  // ===== Modal 控制 =====
  openModal(trigger) {
    if (!this.modal || !this.modalContent) return;

    // 从 trigger 元素获取所有弹窗数据（统一使用 getAttribute 确保正确读取）
    const getDataAttr = (attr) => {
      const value = trigger.getAttribute(attr);
      return value && value.trim() !== '' ? value : '';
    };

    // 检测是否为移动端
    const isMobile = window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT;

    const title = getDataAttr('data-modal-title');
    const content = getDataAttr('data-modal-content');
    const image = getDataAttr('data-modal-image');
    const image2 = getDataAttr('data-modal-image-2');
    const image3 = getDataAttr('data-modal-image-3');
    // 移动端使用 modal_image_mobile，桌面端使用 modal_image_4
    const image4 = isMobile 
      ? getDataAttr('data-modal-image-mobile') 
      : getDataAttr('data-modal-image-4');
    const richtext1 = getDataAttr('data-modal-richtext-1');
    const richtext2 = getDataAttr('data-modal-richtext-2');
    const richtext3 = getDataAttr('data-modal-richtext-3');
    const richtext4 = getDataAttr('data-modal-richtext-4');

    const decodeHtml = (html) => {
      if (!html) return '';
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    };

    // 构建弹窗内容 HTML
    let modalHtml = '';

    // 标题
    if (title) {
      modalHtml += `<h2 class="scroll-intro-modal__title">${title}</h2>`;
    }

    // 描述内容
    if (content) {
      const decodedContent = decodeHtml(content);
      modalHtml += `<div class="scroll-intro-modal__description">${decodedContent}</div>`;
    }

    // 检查是否有任何列表项内容
    const hasListItems = image || richtext1 || richtext2 || image2 || image3 || richtext3 || richtext4 || image4;

    if (hasListItems) {
      modalHtml += '<ul class="scroll-intro-modal__list">';

      // 第一项：图片1 + richtext1
      if (image || richtext1) {
        modalHtml += '<li class="grid">';
        if (image) {
          modalHtml += `
            <div class="grid__item">
              <img
                src="${image}"
                alt="${title}"
                loading="lazy"
                width="100%"
                height="100%">
            </div>`;
        }
        if (richtext1) {
          const decodedRichtext1 = decodeHtml(richtext1);
          modalHtml += `
            <div class="grid__item">
              <div class="scroll-intro-modal__richtext">${decodedRichtext1}</div>
            </div>`;
        }
        modalHtml += '</li>';
      }

      // 第二项：richtext2 + 图片2
      if (richtext2 || image2) {
        modalHtml += '<li class="grid">';
        if (richtext2) {
          const decodedRichtext2 = decodeHtml(richtext2);
          modalHtml += `
            <div class="grid__item">
              <div class="scroll-intro-modal__richtext">${decodedRichtext2}</div>
            </div>`;
        }
        if (image2) {
          modalHtml += `
            <div class="grid__item">
              <img
                src="${image2}"
                alt="${title}"
                loading="lazy"
                width="100%"
                height="100%">
            </div>`;
        }
        modalHtml += '</li>';
      }

      // 第三项：图片3 + richtext3
      if (image3 || richtext3) {
        modalHtml += '<li class="grid">';
        if (image3) {
          modalHtml += `
            <div class="grid__item">
              <img
                src="${image3}"
                alt="${title}"
                loading="lazy"
                width="100%"
                height="100%">
            </div>`;
        }
        if (richtext3) {
          const decodedRichtext3 = decodeHtml(richtext3);
          modalHtml += `
            <div class="grid__item">
              <div class="scroll-intro-modal__richtext">${decodedRichtext3}</div>
            </div>`;
        }
        modalHtml += '</li>';
      }

      // 第四项：richtext4 + 图片4
      if (richtext4 || image4) {
        modalHtml += '<li class="full-width">';
        modalHtml += '<div>';
        if (richtext4) {
          const decodedRichtext4 = decodeHtml(richtext4);
          modalHtml += `<div class="scroll-intro-modal__richtext">${decodedRichtext4}</div>`;
        }
        if (image4) {
          modalHtml += `
            <img
              src="${image4}"
              alt="${title}"
              loading="lazy"
              width="100%"
              height="100%">`;
        }
        modalHtml += '</div>';
        modalHtml += '</li>';
      }

      modalHtml += '</ul>';
    }

    // 如果没有内容，显示提示
    if (!title && !content && !hasListItems) {
      modalHtml = '<p style="color: #ff0000;">暂无内容 (请在Shopify编辑器中配置弹窗内容)</p>';
    }

    this.modalContent.innerHTML = modalHtml;

    this.modal.classList.add('is-open');
    document.body.classList.add('scroll-intro-modal-open');
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.body.classList.remove('scroll-intro-modal-open');
  }

  // ===== 视频控制 =====
  initVideos() {
    if (this.centerVideo) {
      this._centerVideoLoadedHandler = () => { this.centerVideoReady = true; };
      this._centerVideoCanPlayHandler = () => { this.centerVideoReady = true; };
      this.centerVideo.addEventListener('loadeddata', this._centerVideoLoadedHandler);
      this.centerVideo.addEventListener('canplaythrough', this._centerVideoCanPlayHandler);
      this.centerVideo.load();
    }

    if (this.videoPlaceholder && this.videoPlaceholder.tagName === 'VIDEO') {
      this.videoPlaceholder.load();
    }

    this._bgVideoHandlers = [];
    this.bgVideos.forEach((video, index) => {
      if (index === 0) video.load();
      const handler = () => {
        if (index === this.currentModule) this.playVideo(video);
      };
      this._bgVideoHandlers.push({ video, handler });
      video.addEventListener('loadeddata', handler);
    });

    this.thumbVideos.forEach(video => video.load());
  }

  playVideo(video) {
    if (!video) return;

    try {
      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.controls = false;
    } catch (e) {}

    if (video.readyState < 2) {
      video.load();
      video.addEventListener('loadeddata', () => this.playVideo(video), { once: true });
      return;
    }

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        const playOnInteraction = () => {
          video.play().catch(() => {});
          document.removeEventListener('click', playOnInteraction);
          document.removeEventListener('touchstart', playOnInteraction);
        };
        document.addEventListener('click', playOnInteraction, { once: true });
        document.addEventListener('touchstart', playOnInteraction, { once: true });
      });
    }
  }

  pauseVideo(video) {
    if (video) video.pause();
  }

  // ===== 滚动处理 =====
  handleScroll() {
    const progress = this.calculateProgress();
    this.scrollProgress = progress;

    // 检查 phases 是否已初始化
    if (!this.phases || !this.phases.textMerge || !this.phases.showModules) {
      return;
    }

    if (progress <= this.phases.textMerge.end) {
      this.updateTextMergePhase(progress);
    } else if (progress <= this.phases.showModules.end) {
      this.updateShowModulesPhase(progress);
    } else {
      this.updateModuleSwitchPhase(progress);
    }
  }

  calculateProgress() {
    if (!this.section) return 0;

    const rect = this.section.getBoundingClientRect();
    const windowHeight = this.getWindowHeight();
    const sectionHeight = this.section.offsetHeight;

    if (rect.top > 0) return 0;
    if (rect.bottom < windowHeight) return 1;

    const scrolled = Math.abs(rect.top);
    const scrollRange = sectionHeight - windowHeight;
    return scrollRange > 0 ? Math.min(1, Math.max(0, scrolled / scrollRange)) : 0;
  }

  // 缓存窗口尺寸，减少重复查询
  getWindowWidth() {
    const now = Date.now();
    if (now - this._lastWindowSizeCheck > 100) {
      this._windowSize.width = window.innerWidth;
      this._windowSize.height = window.innerHeight;
      this._lastWindowSizeCheck = now;
    }
    return this._windowSize.width;
  }

  getWindowHeight() {
    const now = Date.now();
    if (now - this._lastWindowSizeCheck > 100) {
      this._windowSize.width = window.innerWidth;
      this._windowSize.height = window.innerHeight;
      this._lastWindowSizeCheck = now;
    }
    return this._windowSize.height;
  }

  /**
   * Returns the progress (0–1) that corresponds to the center of the given module index.
   * Used when clicking nav to scroll to that module. Returns null if phases not ready.
   */
  getScrollPositionForModule(index) {
    if (!this.phases?.modules || this.moduleCount <= 0 || index < 0 || index >= this.moduleCount) {
      return null;
    }
    const phaseRange = this.phases.modules.end - this.phases.modules.start;
    const targetProgress = this.phases.modules.start + ((index + 0.5) / this.moduleCount) * phaseRange;
    return Math.max(0, Math.min(1, targetProgress));
  }

  /**
   * Scrolls the window so the section's progress becomes the given value (0–1).
   * Keeps scroll position in sync with handleScroll's calculateProgress().
   */
  scrollToProgress(progress) {
    if (!this.section) return;
    const windowHeight = this.getWindowHeight();
    const sectionHeight = this.section.offsetHeight;
    const scrollRange = sectionHeight - windowHeight;
    if (scrollRange <= 0) return;
    const rect = this.section.getBoundingClientRect();
    const sectionTopDoc = rect.top + window.scrollY;
    const targetScrollY = sectionTopDoc + progress * scrollRange;
    window.scrollTo({ top: targetScrollY, behavior: 'instant' });
  }

  // ===== Phase 0: 文字合并阶段 =====
  updateTextMergePhase(progress) {
    const phaseRange = this.phases.textMerge.end - this.phases.textMerge.start;
    // 防止除零错误
    if (phaseRange <= 0) return;

    const phaseProgress = (progress - this.phases.textMerge.start) / phaseRange;

    this.prepareTextPhase();
    this.updateTextPhaseBackground(phaseProgress);

    const textElements = this.getTextElements();
    if (!textElements.activeTextGroup) return;

    if (this.variant === 'origin') {
      this.updateOriginVariant(phaseProgress, textElements);
      return;
    }

    this.updateStudioVariant(phaseProgress, textElements);
    this.updateVideoState(phaseProgress);
  }

  prepareTextPhase() {
    if (this.initialText) {
      this.initialText.classList.remove('is-hidden');
      this.initialText.style.display = 'block';
    }
    if (this.currentTextGroup !== 0) {
      this.activateTextGroup(0);
    }
  }

  updateTextPhaseBackground(phaseProgress) {
    if (this.variant === 'origin') {
      this.section.classList.toggle('is-text-phase', phaseProgress < 0.7);
    }
  }

  getTextElements() {
    // 缓存查询结果，避免每次滚动都查询 DOM
    if (this._cachedTextElements && this._cachedTextElements.groupIndex === this.currentTextGroup) {
      return this._cachedTextElements;
    }

    const activeTextGroup = this.textGroups[this.currentTextGroup];
    if (!activeTextGroup) {
      this._cachedTextElements = { groupIndex: this.currentTextGroup };
      return {};
    }

    const elements = {
      groupIndex: this.currentTextGroup,
      activeTextGroup,
      textLine1: activeTextGroup.querySelector('.scroll-intro-modules__text-line--1'),
      textLine2: activeTextGroup.querySelector('.scroll-intro-modules__text-line--2'),
      textPartBefore: activeTextGroup.querySelector('.scroll-intro-modules__text-part--before'),
      textPartAfter: activeTextGroup.querySelector('.scroll-intro-modules__text-part--after')
    };

    this._cachedTextElements = elements;
    return elements;
  }

  // ===== Origin 变体处理 =====
  updateOriginVariant(phaseProgress, { textLine2, textPartBefore, textPartAfter }) {
    this.setupMarquee(textLine2, textPartBefore, textPartAfter);

    if (phaseProgress < 0.25) {
      this.showInitialText();
    } else if (phaseProgress < 0.55) {
      this.showInitialText();
      this.updateCenterVideoSize(phaseProgress, 0.25, 0.3, 140, Math.min(this.getWindowWidth() * 0.6, 700));
    } else if (phaseProgress < 0.7) {
      this.updateCenterVideoFullscreen(phaseProgress);
      this.fadeOutText(phaseProgress);
    } else {
      this.showFullscreenVideo();
      // 只有当有中心视频时才播放
      if (this.centerVideo) {
        this.playCenterVideo();
      }
    }

    this.hideModulesContent();
    this.updateBackgroundVideos(phaseProgress);
    this.resetCenterVideoIfNeeded(phaseProgress);
  }

  setupMarquee(textLine2, textPartBefore, textPartAfter) {
    if (!textLine2 || this._marqueeSetupDone) return;

    const marqueeAttr = textLine2.dataset?.marqueeText;
    let fullText = (marqueeAttr || '').trim();
    if (!fullText) {
      const beforeText = textPartBefore ? textPartBefore.textContent.trim() : '';
      const afterText = textPartAfter ? textPartAfter.textContent.trim() : '';
      fullText = (beforeText + ' ' + afterText).replace(/\s+/g, ' ').trim();
    }
    const repeats = Array(CONSTANTS.MARQUEE_REPEATS).fill(fullText).join(' • ');
    textLine2.innerHTML = `<span class="sim-marquee-track">${repeats}</span>`;
    this._marqueeSetupDone = true;
  }

  // ===== Studio 变体处理 =====
  updateStudioVariant(phaseProgress, { textLine1, textLine2, textPartBefore, textPartAfter }) {
    if (phaseProgress < 0.15) {
      this.resetToInitialState(textLine1, textLine2, textPartBefore, textPartAfter);
    } else if (phaseProgress < 0.25) {
      this.mergeTextLines(phaseProgress, textLine1, textLine2, textPartBefore, textPartAfter);
    } else if (phaseProgress < 0.55) {
      this.showVideoWithText(phaseProgress, textLine1, textPartBefore, textPartAfter);
    } else if (phaseProgress < 0.7) {
      this.expandVideoToFullscreen(phaseProgress, textPartBefore, textPartAfter);
    } else {
      this.showFullscreenVideo();
      // 只有当有中心视频时才播放
      if (this.centerVideo) {
        this.playCenterVideo();
      }
    }
  }

  resetToInitialState(textLine1, textLine2, textPartBefore, textPartAfter) {
    this.showInitialText();

    if (textLine1) {
      textLine1.style.opacity = '1';
      textLine1.style.transform = 'translateY(0)';
      textLine1.style.display = 'block';
    }
    if (textLine2) {
      textLine2.style.opacity = '1';
      textLine2.style.transform = 'translateY(0)';
      textLine2.style.display = 'block';
    }

    this.resetTextParts(textPartBefore, textPartAfter);
    this.hideCenterMedia();
  }

  resetTextParts(textPartBefore, textPartAfter) {
    [textPartBefore, textPartAfter].forEach(part => {
      if (!part) return;
      part.style.opacity = '1';
      part.style.transform = 'translateX(0)';
      part.style.marginRight = '';
      part.style.marginLeft = '';
    });
  }

  mergeTextLines(phaseProgress, textLine1, textLine2, textPartBefore, textPartAfter) {
    const stage1Progress = (phaseProgress - 0.15) / 0.1;
    this.showInitialText();

    if (stage1Progress < 0.7) {
      this.animateTwoLines(stage1Progress, textLine1, textLine2, textPartBefore, textPartAfter);
    } else {
      this.fadeInSingleLine(stage1Progress, textLine1, textPartBefore, textPartAfter);
    }

    this.hideCenterMedia();
  }

  animateTwoLines(progress, textLine1, textLine2, textPartBefore, textPartAfter) {
    if (textLine1) {
      textLine1.style.display = 'block';
      textLine1.style.opacity = 1 - (progress / 0.7);
      textLine1.style.transform = `translateY(-${(progress / 0.7) * 20}px)`;
    }
    if (textLine2) {
      textLine2.style.display = 'block';
      textLine2.style.opacity = '1';
      textLine2.style.transform = `translateY(-${50 * (progress / 0.7)}px)`;
    }

    [textPartBefore, textPartAfter].forEach(part => {
      if (!part) return;
      part.style.opacity = '0';
      part.style.marginRight = '0px';
      part.style.marginLeft = '0px';
      part.style.transform = 'translateX(0)';
    });
  }

  fadeInSingleLine(progress, textLine1, textPartBefore, textPartAfter) {
    if (textLine1) {
      textLine1.style.opacity = '0';
      textLine1.style.display = 'none';
    }

    const fadeProgress = (progress - 0.7) / 0.3;
    [textPartBefore, textPartAfter].forEach(part => {
      if (!part) return;
      part.style.transform = 'translateX(0)';
      part.style.marginRight = '0px';
      part.style.marginLeft = '0px';
      part.style.opacity = fadeProgress;
    });
  }

  showVideoWithText(phaseProgress, textLine1, textPartBefore, textPartAfter) {
    this.showInitialText();
    if (textLine1) {
      textLine1.style.opacity = '0';
      textLine1.style.display = 'none';
    }

    const stage2Progress = (phaseProgress - 0.25) / 0.3;
    this.updateCenterVideoSize(phaseProgress, 0.25, 0.3, 140, Math.min(this.getWindowWidth() * 0.6, 700));
    this.pushTextApart(stage2Progress, textPartBefore, textPartAfter);
  }

  pushTextApart(progress, textPartBefore, textPartAfter) {
    const windowWidth = this.getWindowWidth();
    const aspectRatio = CONSTANTS.ASPECT_RATIO;
    const startW = CONSTANTS.VIDEO_START_WIDTH;
    const endW = Math.min(windowWidth * CONSTANTS.VIDEO_MAX_WIDTH_RATIO, CONSTANTS.VIDEO_MAX_WIDTH);
    const videoWidth = startW + (endW - startW) * progress;
    const halfWidth = videoWidth / 2;
    const spacing = CONSTANTS.TEXT_SPACING;

    if (textPartBefore) {
      textPartBefore.style.transform = `translateX(-${halfWidth + spacing}px)`;
      textPartBefore.style.marginRight = '0';
      textPartBefore.style.opacity = '1';
    }
    if (textPartAfter) {
      textPartAfter.style.transform = `translateX(${halfWidth + spacing}px)`;
      textPartAfter.style.marginLeft = '0';
      textPartAfter.style.opacity = '1';
    }
  }

  expandVideoToFullscreen(phaseProgress, textPartBefore, textPartAfter) {
    const stage3Progress = (phaseProgress - 0.55) / 0.15;
    const windowWidth = this.getWindowWidth();
    const windowHeight = this.getWindowHeight();
    const aspectRatio = CONSTANTS.ASPECT_RATIO;

    const startWidth = Math.min(windowWidth * CONSTANTS.VIDEO_MAX_WIDTH_RATIO, CONSTANTS.VIDEO_MAX_WIDTH);
    const startHeight = startWidth / aspectRatio;

    let endWidth, endHeight;
    if (windowWidth / windowHeight > aspectRatio) {
      endWidth = windowWidth;
      endHeight = windowWidth / aspectRatio;
    } else {
      endHeight = windowHeight;
      endWidth = windowHeight * aspectRatio;
    }

    const currentWidth = startWidth + (endWidth - startWidth) * stage3Progress;
    const currentHeight = startHeight + (endHeight - startHeight) * stage3Progress;

    if (this.centerMedia) {
      this.centerMedia.style.display = 'block';
      this.centerMedia.style.opacity = '1';
      this.centerMedia.style.width = `${currentWidth}px`;
      this.centerMedia.style.height = `${currentHeight}px`;
      this.centerMedia.style.borderRadius = `${CONSTANTS.BORDER_RADIUS * (1 - stage3Progress)}px`;
    }

    const halfWidth = currentWidth / 2;
    const baseSpacing = CONSTANTS.TEXT_SPACING;
    const additionalSpacing = 100 * stage3Progress;
    const totalSpacing = baseSpacing + additionalSpacing;

    if (textPartBefore) {
      textPartBefore.style.opacity = 1 - stage3Progress;
      textPartBefore.style.transform = `translateX(-${halfWidth + totalSpacing}px)`;
    }
    if (textPartAfter) {
      textPartAfter.style.opacity = 1 - stage3Progress;
      textPartAfter.style.transform = `translateX(${halfWidth + totalSpacing}px)`;
    }
  }

  // ===== 视频状态控制 =====
  updateCenterVideoSize(phaseProgress, startPhase, duration, startWidth, endWidth) {
    if (!this.centerMedia || duration <= 0) return;

    const progress = Math.max(0, Math.min(1, (phaseProgress - startPhase) / duration));
    const windowWidth = this.getWindowWidth();
    const aspectRatio = CONSTANTS.ASPECT_RATIO;
    const videoWidth = startWidth + (endWidth - startWidth) * progress;
    const videoHeight = videoWidth / aspectRatio;

    this.centerMedia.style.display = 'block';
    this.centerMedia.style.opacity = '1';
    this.centerMedia.style.width = `${videoWidth}px`;
    this.centerMedia.style.height = `${videoHeight}px`;
  }

  updateCenterVideoFullscreen(phaseProgress) {
    if (!this.centerMedia) return;

    const stage3Progress = Math.max(0, Math.min(1, (phaseProgress - 0.55) / 0.15));
    const windowWidth = this.getWindowWidth();
    const windowHeight = this.getWindowHeight();
    const aspectRatio = CONSTANTS.ASPECT_RATIO;

    const startWidth = Math.min(windowWidth * CONSTANTS.VIDEO_MAX_WIDTH_RATIO, CONSTANTS.VIDEO_MAX_WIDTH);
    const startHeight = startWidth / aspectRatio;

    let endWidth, endHeight;
    if (windowWidth / windowHeight > aspectRatio) {
      endWidth = windowWidth;
      endHeight = windowWidth / aspectRatio;
    } else {
      endHeight = windowHeight;
      endWidth = windowHeight * aspectRatio;
    }

    const currentWidth = startWidth + (endWidth - startWidth) * stage3Progress;
    const currentHeight = startHeight + (endHeight - startHeight) * stage3Progress;

    this.centerMedia.style.display = 'block';
    this.centerMedia.style.opacity = '1';
    this.centerMedia.style.width = `${currentWidth}px`;
    this.centerMedia.style.height = `${currentHeight}px`;
    this.centerMedia.style.borderRadius = `${CONSTANTS.BORDER_RADIUS * (1 - stage3Progress)}px`;
  }

  showFullscreenVideo() {
    if (!this.centerMedia) return;

    this.centerMedia.style.opacity = '1';
    this.centerMedia.style.width = `${this.getWindowWidth()}px`;
    this.centerMedia.style.height = `${this.getWindowHeight()}px`;
    this.centerMedia.style.borderRadius = '0px';

    this.bgVideos.forEach(video => {
      video.style.opacity = 0;
      this.pauseVideo(video);
      video.classList.remove('is-active');
    });

    this.bgImages.forEach(image => {
      image.style.opacity = 0;
      image.classList.remove('is-active');
    });
  }

  playCenterVideo() {
    if (!this.centerVideoPlaying && this.centerVideo) {
      this.centerVideo.play().then(() => {
        if (this.videoPlaceholder) {
          setTimeout(() => this.videoPlaceholder.classList.add('is-hidden'), 100);
        }
      }).catch(() => {});
      this.centerVideoPlaying = true;
    }
  }

  hideCenterMedia() {
    if (this.centerMedia) {
      this.centerMedia.style.opacity = '0';
      this.centerMedia.style.width = '0px';
      this.centerMedia.style.height = '0px';
      this.centerMedia.style.display = 'none';
    }
  }

  showInitialText() {
    if (this.initialText) {
      this.initialText.classList.remove('is-hidden');
      this.initialText.style.opacity = '1';
      this.initialText.style.display = 'block';
    }
  }

  fadeOutText(phaseProgress) {
    if (this.initialText) {
      this.initialText.style.opacity = String(1 - ((phaseProgress - 0.55) / 0.15));
    }
  }

  hideModulesContent() {
    if (this.modulesContent) {
      this.modulesContent.classList.remove('is-visible');
      this.modulesContent.style.opacity = 0;
    }
  }

  updateBackgroundVideos(phaseProgress) {
    if (phaseProgress < 0.7) {
      this.bgVideos.forEach(video => { video.style.opacity = 0; });
      this.bgImages.forEach(image => { image.style.opacity = 0; });
    }
  }

  updateVideoState(phaseProgress) {
    if (this.modulesContent) {
      this.modulesContent.classList.remove('is-visible');
    }

    if (phaseProgress < 0.7 && this.centerVideoPlaying) {
      this.centerVideoPlaying = false;
      if (this.centerVideo) {
        this.centerVideo.pause();
        this.centerVideo.currentTime = 0;
      }
      if (this.videoPlaceholder) {
        this.videoPlaceholder.classList.remove('is-hidden');
      }
      if (phaseProgress < 0.25 && this.centerVideo) {
        this.centerVideo.load();
      }
    }

    if (phaseProgress < 0.25 && this.centerMedia) {
      this.centerMedia.style.display = 'none';
      if (this.videoPlaceholder) {
        this.videoPlaceholder.classList.remove('is-hidden');
      }
    }

    if (phaseProgress < 0.7) {
      this.bgVideos.forEach(video => { video.style.opacity = 0; });
      this.bgImages.forEach(image => { image.style.opacity = 0; });
      if (this.modulesContent) {
        this.modulesContent.style.opacity = 0;
      }
    }
  }

  resetCenterVideoIfNeeded(phaseProgress) {
    if (phaseProgress < 0.7 && this.centerVideoPlaying) {
      this.centerVideoPlaying = false;
      if (this.centerVideo) {
        this.centerVideo.pause();
        this.centerVideo.currentTime = 0;
      }
      if (this.videoPlaceholder) {
        this.videoPlaceholder.classList.remove('is-hidden');
      }
      if (phaseProgress < 0.25 && this.centerVideo) {
        this.centerVideo.load();
      }
    }

    if (phaseProgress < 0.25 && this.centerMedia) {
      this.centerMedia.style.display = 'none';
      if (this.videoPlaceholder) {
        this.videoPlaceholder.classList.remove('is-hidden');
      }
    }
  }

  // ===== Phase 1: 显示模块界面 =====
  updateShowModulesPhase(progress) {
    const phaseRange = this.phases.showModules.end - this.phases.showModules.start;
    if (phaseRange <= 0) return;

    const phaseProgress = (progress - this.phases.showModules.start) / phaseRange;

    if (this.variant === 'origin') {
      this.section.classList.remove('is-text-phase');
    }

    if (this.initialText) {
      this.initialText.classList.add('is-hidden');
    }

    if (this.centerMedia) {
      this.centerMedia.style.opacity = 1 - phaseProgress;
    }

    if (this.modulesContent) {
      this.modulesContent.classList.add('is-visible');
      this.modulesContent.style.opacity = phaseProgress;
    }

    // 缓存背景容器查询
    if (!this._cachedBgContainer) {
      this._cachedBgContainer = this.section.querySelector('.scroll-intro-modules__background');
    }
    if (this._cachedBgContainer) {
      this._cachedBgContainer.style.opacity = 1;
    }

    this.activateModule(this.currentModule);

    if (phaseProgress < 0.2) {
      this.bgVideos.forEach(video => this.pauseVideo(video));
    } else {
      if (this.bgVideos[0] && this.currentModule !== 0) {
        this.activateModule(0);
      }
    }
  }

  // ===== Phase 2+: 模块切换 =====
  updateModuleSwitchPhase(progress) {
    const phaseStart = this.phases.modules.start;
    const phaseEnd = this.phases.modules.end;
    const phaseRange = phaseEnd - phaseStart;
    if (phaseRange <= 0 || this.moduleCount === 0) return;

    const phaseProgress = (progress - phaseStart) / phaseRange;

    if (this.variant === 'origin') {
      this.section.classList.remove('is-text-phase');
    }

    if (this.initialText) {
      this.initialText.classList.add('is-hidden');
      this.initialText.style.opacity = 0;
      this.initialText.style.display = 'none';
    }

    if (this.modulesContent) {
      this.modulesContent.classList.add('is-visible');
      this.modulesContent.style.opacity = 1;
    }

    if (this.centerMedia) {
      this.centerMedia.style.opacity = 0;
      this.centerMedia.style.display = 'none';
    }

    // 缓存背景容器查询
    if (!this._cachedBgContainer) {
      this._cachedBgContainer = this.section.querySelector('.scroll-intro-modules__background');
    }
    if (this._cachedBgContainer) {
      this._cachedBgContainer.style.opacity = 1;
    }

    this.bgVideos.forEach(video => {
      video.style.removeProperty('opacity');
    });

    this.bgImages.forEach(image => {
      image.style.removeProperty('opacity');
    });

    const moduleIndex = Math.floor(phaseProgress * this.moduleCount);
    const targetModule = Math.min(moduleIndex, this.moduleCount - 1);

    if (targetModule !== this.currentModule) {
      this.switchToModule(targetModule);
    }
  }

  // ===== 模块控制 =====
  switchToModule(index) {
    if (index === this.currentModule || index < 0 || index >= this.moduleCount) {
      return;
    }
    this.currentModule = index;
    this.activateModule(index);
  }

  activateTextGroup(index) {
    if (index === this.currentTextGroup) return;

    this.currentTextGroup = index;
    // 清除文本元素缓存，因为切换了文字组
    this._cachedTextElements = null;

    this.textGroups.forEach((group, i) => {
      group.classList.toggle('is-active', i === index);
    });
  }

  activateModule(index) {
    this.activateTextGroup(index);

    // 处理背景视频
    this.bgVideos.forEach((video, i) => {
      if (i === index) {
        video.classList.add('is-active');
        this.playVideo(video);
      } else {
        video.classList.remove('is-active');
        this.pauseVideo(video);
      }
    });

    // 处理背景图片
    this.bgImages.forEach((image, i) => {
      if (i === index) {
        image.classList.add('is-active');
        image.style.opacity = '1';
      } else {
        image.classList.remove('is-active');
        image.style.opacity = '0';
      }
    });

    this.moduleDetails.forEach((detail, i) => {
      detail.classList.toggle('is-active', i === index);
    });

    this.navTitles.forEach((title, i) => {
      title.classList.toggle('is-active', i === index);
    });

    if (this.variant === 'origin' && this.currentTitleEl) {
      const titleText = (this.navTitles[index]?.textContent || '').trim();
      this.currentTitleEl.textContent = titleText;
    }

    this.updateThumbnails(index);
  }

  updateThumbnails(index) {
    const pairedIndex = this.getPairedIndex(index);

    // 缓存 thumbItems 查询结果
    if (!this._cachedThumbItems) {
      this._cachedThumbItems = this.section.querySelectorAll('[data-thumb-item]');
    }

    this._cachedThumbItems.forEach((item, i) => {
      const isActive = (i === pairedIndex);
      const isAdjacent = (i === pairedIndex - 1) || (i === pairedIndex + 1);
      item.classList.toggle('is-active', isActive);
      item.classList.toggle('is-adjacent', !isActive && isAdjacent);
    });

    this.navThumbs.forEach((thumb, i) => {
      const isActive = (i === pairedIndex);
      const isAdjacent = (i === pairedIndex - 1) || (i === pairedIndex + 1);
      thumb.classList.toggle('is-active', isActive);
      thumb.classList.toggle('is-adjacent', !isActive && isAdjacent);
    });
  }

  getPairedIndex(index) {
    const max = this.moduleCount - 1;
    const paired = max - index;
    return Math.max(0, Math.min(max, paired));
  }

  // ===== 移动端 =====
  initMobile() {
    this.mobileSlider = this.section.querySelector('.scroll-intro-modules__mobile-slider');
    this.mobileSlides = this.section.querySelector('.scroll-intro-modules__mobile-slides');
    this.mobileSlideElements = this.section.querySelectorAll('.scroll-intro-modules__mobile-slide');
    if (!this.mobileSlideElements || this.mobileSlideElements.length === 0) {
      this.mobileSlideElements = this.section.querySelectorAll('[data-mobile-slide]');
    }
    this.mobileVideos = this.section.querySelectorAll('.scroll-intro-modules__mobile-media-video');

    if (!this.mobileSlider || !this.mobileSlides) return;

    this.markMobileSlideTypes();
    this.bindMobileModalEvents();

    this.mobileActiveIndex = 0;
    this.applyMobileActiveState(0);

    let rafId = null;
    let debounceId = null;
    this._mobileScrollHandler = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        const idx = this.getClosestMobileIndex();
        if (idx !== this.mobileActiveIndex) {
          this.mobileActiveIndex = idx;
          this.applyMobileActiveState(idx);
        }
      });

      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        this.snapToMobileIndex(this.mobileActiveIndex);
      }, CONSTANTS.SNAP_DEBOUNCE);
    };
    this.mobileSlides.addEventListener('scroll', this._mobileScrollHandler, { passive: true });
  }

  markMobileSlideTypes() {
    this.mobileSlideElements.forEach(slide => {
      const videoEl = slide.querySelector('.scroll-intro-modules__mobile-media-video');
      const imageEl = slide.querySelector('.scroll-intro-modules__mobile-media-image');
      const hasVideoSources = !!videoEl?.querySelector('source');
      if (videoEl && hasVideoSources) {
        slide.classList.add('has-video');
      } else if (imageEl) {
        slide.classList.add('has-image');
      }
    });
  }

  bindMobileModalEvents() {
    try {
      this.mobileSlideElements.forEach(slide => {
        const label = slide.querySelector('.scroll-intro-modules__mobile-cta-label');
        const plus = slide.querySelector('.scroll-intro-modules__mobile-cta-icon');

        const handler = (triggerEl) => (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openModal(triggerEl);
        };

        if (label) label.addEventListener('click', handler(label));
        if (plus) plus.addEventListener('click', handler(plus));
      });
    } catch (err) {
      // 静默处理错误
    }
  }

  getClosestMobileIndex() {
    if (!this.mobileSlides || !this.mobileSlideElements?.length) return 0;
    const scrollLeft = this.mobileSlides.scrollLeft;
    let minDist = Infinity;
    let closest = 0;
    this.mobileSlideElements.forEach((slide, i) => {
      const left = slide.offsetLeft;
      const dist = Math.abs(left - scrollLeft);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    return closest;
  }

  applyMobileActiveState(index) {
    if (!this.mobileSlideElements?.length) return;
    this.mobileSlideElements.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);

      const video = slide.querySelector('.scroll-intro-modules__mobile-media-video');
      if (video) {
        if (isActive) {
          this.playVideo(video);
        } else {
          this.pauseVideo(video);
          try { video.currentTime = 0; } catch (e) {}
        }
      }
    });
  }

  snapToMobileIndex(index) {
    const target = this.mobileSlideElements?.[index];
    if (!this.mobileSlides || !target) return;
    this.mobileSlides.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  // ===== 清理 =====
  destroy() {
    // 移除滚动监听器
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }

    // 移除 resize 监听器
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    // 移除 Modal 监听器
    if (this._modalKeyHandler) {
      document.removeEventListener('keydown', this._modalKeyHandler);
      this._modalKeyHandler = null;
    }

    if (this._modalClickHandler && this.modal) {
      this.modal.removeEventListener('click', this._modalClickHandler);
      this._modalClickHandler = null;
    }

    // 移除移动端滚动监听器
    if (this._mobileScrollHandler && this.mobileSlides) {
      this.mobileSlides.removeEventListener('scroll', this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }

    // 清理 IntersectionObserver
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // 移除视频事件监听器
    if (this.centerVideo) {
      if (this._centerVideoLoadedHandler) {
        this.centerVideo.removeEventListener('loadeddata', this._centerVideoLoadedHandler);
      }
      if (this._centerVideoCanPlayHandler) {
        this.centerVideo.removeEventListener('canplaythrough', this._centerVideoCanPlayHandler);
      }
      this.centerVideo.pause();
    }

    // 移除背景视频事件监听器
    if (this._bgVideoHandlers) {
      this._bgVideoHandlers.forEach(({ video, handler }) => {
        video.removeEventListener('loadeddata', handler);
        video.pause();
      });
      this._bgVideoHandlers = null;
    }

    // 暂停所有视频
    this.bgVideos.forEach(video => video.pause());

    if (this.videoPlaceholder && this.videoPlaceholder.tagName === 'VIDEO') {
      this.videoPlaceholder.pause();
    }

    if (this.mobileVideos) {
      this.mobileVideos.forEach(video => video.pause());
    }

    // 清理缓存
    this._cachedThumbItems = null;
    this._cachedTextElements = null;
    this._cachedBgContainer = null;
  }
}

// ===== 初始化 =====
function initScrollIntroModules() {
  const sections = document.querySelectorAll('.scroll-intro-modules');
  const instances = [];

  sections.forEach(section => {
    const instance = new ScrollIntroModules(section);
    instances.push(instance);
  });

  window.scrollIntroModulesInstances = instances;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollIntroModules);
} else {
  initScrollIntroModules();
}

// ===== Shopify 主题编辑器支持 =====
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('.scroll-intro-modules');
    if (section) {
      new ScrollIntroModules(section);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('.scroll-intro-modules');
    if (section && window.scrollIntroModulesInstances) {
      const instance = window.scrollIntroModulesInstances.find(
        inst => inst.section === section
      );
      if (instance) {
        instance.destroy();
        const index = window.scrollIntroModulesInstances.indexOf(instance);
        window.scrollIntroModulesInstances.splice(index, 1);
      }
    }
  });
}
